import {isEqual} from 'lodash'

export type Filter<T> = (item: T) => boolean
export type Updater<T> = (item: T) => void

export type IdSelector<T, K> = (item: T) => K

export function updateItemsByFilter<T>(items: T[], filter: Filter<T>, updater: Updater<T>): void {
  items.forEach(item => filter(item) && updater(item))
}

const defaultIdSelector = (item: any) => item.id

export function insertItemsById<T, K>(
  items: T[],
  newItems: T[],
  updateExistingOnly: boolean = false,
  idSelector: IdSelector<T, K> = defaultIdSelector
): void {
  if (newItems.length === 0) {
    return
  }

  const newItemsMap = new Map(newItems.map(item => [idSelector(item), item]))

  items.forEach((item, index) => {
    const id = idSelector(item)
    const newItem = newItemsMap.get(id)
    if (!newItem) {
      return
    }

    items[index] = newItem
    newItemsMap.delete(id)
  })

  if (!updateExistingOnly && newItemsMap.size) {
    items.push(...Array.from(newItemsMap.values()))
  }
}

export function removeItemsByFilter<T>(items: T[], filter: Filter<T>): void {
  for (let i = items.length - 1; i >= 0; i--) {
    if (filter(items[i])) {
      items.splice(i, 1)
    }
  }
}

export function removeItemsById<T>(
  items: T[],
  idsToRemove: string[],
  idSelector: IdSelector<T, any> = defaultIdSelector
) {
  const idsToRemoveSet = new Set(idsToRemove)
  removeItemsByFilter(items, (item) => idsToRemoveSet.has(idSelector(item)))
}

export function removeItems<T>(items: T[], itemsToRemove: T[], idSelector = defaultIdSelector) {
  const idsToRemove = itemsToRemove.map(idSelector)
  removeItemsById(items, idsToRemove, idSelector)
}

export function updateItemById<T, K>(
  items: T[],
  id: K,
  updater: Updater<T>,
  idSelector: IdSelector<T, K> = defaultIdSelector
) {
  const item = items.find(i => idSelector(i) === id)
  if (item !== undefined) {
    updater(item)
  }
}

export function moveItemInArray<T>(arr: T[], from: number, to: number) {
  if (from < 0 || to < 0 || from === to) {
    return
  }
  const element = arr[from]
  arr.splice(from, 1)
  arr.splice(to, 0, element)
}

export function updateItemsByIds<T extends {id: number | string}>(
  items: T[],
  itemsToUpdate: T[],
  updater: (item: T) => void
) {
  const itemIds = itemsToUpdate.map(item => item.id)

  updateItemsByFilter(items, ({id}) => itemIds.includes(id), updater)
}

export const getNumericIndex = <T>(array: T[], index: number | string): number => {
  switch (index) {
    case 'start':
      return 0

    case 'end':
      return array.length - 1

    default:
      return index as number
  }
}

export const repositionItem = <T>(items: T[], oldIndex: number | string, newIndex: number | string): void => {
  if (!items.length) {
    return
  }

  const oldNumericIndex = getNumericIndex(items, oldIndex)
  const newNumericIndex = getNumericIndex(items, newIndex)

  if (oldNumericIndex === newNumericIndex) {
    return
  }

  const item = items.splice(oldNumericIndex, 1)[0]
  items.splice(newNumericIndex, 0, item)
}

export const reorderItems = <T>(items: T[], indexMap: number[]): void => {
  if (!items.length || items.length !== indexMap.length) {
    return
  }

  const targetIndex = indexMap.map(item => items[item])

  if (isEqual(targetIndex, items)) {
    return
  }

  items.sort((a, b) => {
    const ia = targetIndex.indexOf(a)
    const ib = targetIndex.indexOf(b)
    return (ia === -1 || ib === -1) ? 0 : ia - ib
  })
}